<template>
  <div class="app-container">
    <!-- 1. 顶部筛选栏 -->
    <div class="filter-container">
      <div class="filter-item">
        <span class="label">{{ $t('newOrder.createTime') }}</span>
        <el-date-picker
          v-model="timeValue"
          :clearable="false"
          :default-time="['00:00:00', '23:59:59']"
          :format="dateFormat"
          :picker-options="pickerOptions"
          :placeholder="$t('newOrder.SelectDatetime')"
          range-separator="~"
          style="width: 300px"
          type="datetimerange"
          value-format="yyyy-MM-dd HH:mm:ss"
        />
      </div>
      <div class="filter-item">
        <span class="label">{{ $t('newOrder.CustomerName') }}</span>
        <el-select
          v-model="queryParams.customerId"
          :placeholder="$t('GlobalSetObj.OnlyAreDisplayed')"
          :remote-method="getCustomerPage"
          clearable
          filterable
          remote
          reserve-keyword
          @focus="getCustomerPage('')"
        >
          <el-option
            v-for="(item, index) in customerList"
            :key="index"
            :label="`${item.customerAlias}（${item.username}）`"
            :value="item.id"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="label">{{ $t('newOrder.SenderState') }}</span>
        <el-select
          v-model="queryParams.senderStateList"
          :placeholder="$t('GlobalSetObj.pleaseChoose')"
          clearable
          filterable
          multiple
        >
          <el-option
            v-for="item in StateList"
            :key="item.id"
            :label="item.label"
            :value="item.label"
          />
        </el-select>
      </div>
      <div class="filter-actions">
        <el-button
          icon="el-icon-search" size="small" type="primary"
          @click="getSearch"
        >
          {{ $t('newOrder.search') }}
        </el-button>
        <el-button size="small" @click="handleReset">{{ $t('newOrder.reset') }}</el-button>
        <el-radio-group v-model="queryParams.isTest" size="small" style="margin: 0 10px">
          <el-radio-button :label="1">{{ $t('newOrder.Formal') }}</el-radio-button>
          <el-radio-button :label="2">{{ $t('newOrder.test1') }}</el-radio-button>
        </el-radio-group>
        <el-button
          icon="el-icon-upload2" size="small" type="primary"
          @click="exportFun"
        >
          {{ $t('newOrder.export') }}
        </el-button>
      </div>
    </div>
    <!-- 2. 实时总数/预警卡片 -->
    <el-row :gutter="20" class="summary-row">
      <el-col v-for="card in summaryCards" :key="card.key" :span="card.span">
        <div
          :class="[
            'stat-card',
            card.type === 'summary' ? 'summary-card' : 'warn-card',
            card.borderClass,
            card.bgClass
          ]"
        >
          <div class="card-content">
            <div class="warn-label">{{ card.label }}</div>
            <div class="warn-desc">{{ card.desc }}</div>
            <div :class="['warn-number', card.numberClass]">{{ card.value }}</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 3. 各大区明细 -->
    <div class="region-section">
      <div class="section-header text-green font-bold">
        {{ $t('tablePrice.pendingPickupByRegion') }}
      </div>
      <el-row :gutter="20">
        <el-col
          v-for="(item, index) in regionData"
          :key="index"
          :span="6"
          style="margin-bottom: 20px"
        >
          <div class="region-card border-green">
            <div class="region-name font-bold">{{ item.name }}</div>
            <div class="region-stat">
              <span class="stat-label">{{ $t('tablePrice.pendingPickupWaybillPackage') }}</span>
              <span class="stat-number text-green">{{ item.pendingCount }}</span>
            </div>
            <div class="region-stat">
              <span class="stat-label">{{ $t('tablePrice.orderCustomerCount') }}</span>
              <span class="stat-number text-green">{{ item.customerCount }}</span>
            </div>
            <div v-if="item.customerCount > 0 || item.pendingCount > 0" class="region-footer">
              <i class="el-icon-bottom text-green" @click="handleExport(item)" />
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    <div class="region-section" style="width: 100%; margin-top: 20px">
      <div v-show="packageNumberList.length > 0" ref="chartDom" style="height: 300px" />
      <div v-show="packageNumberList.length === 0">
        <el-empty :description="$t('tablePrice.noData')" />
      </div>
    </div>
  </div>
</template>

<script>
import { bulkyWasteCollectBoardQuery, getLgAreaListData } from '@/api/orderApi';
import dayjs from 'dayjs';
import { apiCustomerPage } from '@/api/customer';
import { goResourceTaskList } from '@/utils/goExportList';
import { bulkyWasteCollectBoardExport, waybillExportAll } from '@/api/bulkExpressApi';
import * as echarts from 'echarts';
// ✅ 大区字段映射配置（消除硬编码）
const REGION_FIELD_MAP = [
  {
    key: 'santaCruz',
    customerKey: 'santaCruzCustomer',
    label: 'tablePrice.saopauloRegion',
    regionId: 1
  },
  {
    key: 'southeast',
    customerKey: 'southeastCustomer',
    label: 'tablePrice.southeastRegion',
    regionId: 3
  },
  { key: 'south', customerKey: 'southCustomer', label: 'tablePrice.southRegion', regionId: 4 },
  { key: 'north', customerKey: 'northCustomer', label: 'tablePrice.northRegion', regionId: 5 },
  {
    key: 'northeast',
    customerKey: 'northeastCustomer',
    label: 'tablePrice.northeastRegion',
    regionId: 6
  },
  {
    key: 'northwest',
    customerKey: 'northwestCustomer',
    label: 'tablePrice.northwestRegion',
    regionId: 7
  },
  {
    key: 'centralWest',
    customerKey: 'centralWestCustomer',
    label: 'tablePrice.midwestRegion',
    regionId: 8
  }
];

export default {
  name: 'PickupDashboard',
  data() {
    return {
      zhDate: 'yyyy-MM-dd HH:mm:ss',
      ptDate: 'dd-MM-yyyy HH:mm:ss',
      timeValue: [],
      pickerMinDate: null,
      customerList: [],
      StateList: [],
      queryParams: {
        customerId: '',
        senderStateList: [],
        isTest: 1
      },
      regionData: [],
      resultData: {},
      customerNames: [],
      packageNumberList: []
    };
  },
  computed: {
    // ✅ 日期格式提取为计算属性
    dateFormat() {
      return this.$i18n.locale === 'zh' ? this.zhDate : this.ptDate;
    },
    // 限制只能选择一个月内的日期范围
    pickerOptions() {
      const that = this;
      return {
        onPick: ({ minDate, maxDate }) => {
          that.pickerMinDate = minDate ? new Date(minDate) : null;
          if (maxDate) {
            that.pickerMinDate = null;
          }
        },
        disabledDate(time) {
          if (that.pickerMinDate) {
            const range = 30 * 24 * 3600 * 1000;
            const min = that.pickerMinDate.getTime() - range;
            const max = that.pickerMinDate.getTime() + range;
            return time.getTime() < min || time.getTime() > max;
          }
          return false;
        }
      };
    },
    // ✅ 统计卡片配置化（消除模板重复）
    summaryCards() {
      return [
        {
          key: 'today',
          span: 5,
          type: 'warn',
          borderClass: 'border-green',
          bgClass: 'bg-light-green',
          numberClass: 'text-green',
          label: this.$t('newOrder.today'),
          desc: this.$t('tablePrice.notPickedUpCount'),
          value: this.resultData.today || 0
        },
        {
          key: 'total',
          span: 6,
          type: 'summary',
          borderClass: 'border-green',
          bgClass: '',
          numberClass: 'text-red',
          label: this.$t('tablePrice.realtimeTotal'),
          desc: this.$t('tablePrice.pendingPickupTotal'),
          value: this.resultData.totalQuantity || 0
        },
        {
          key: 'over7',
          span: 4,
          type: 'warn',
          borderClass: 'border-red',
          bgClass: 'bg-pink',
          numberClass: 'text-red',
          label: this.$t('tablePrice.over7Days'),
          desc: this.$t('tablePrice.notPickedUpCount'),
          value: this.resultData.overSevenDays || 0
        },
        {
          key: '7to3',
          span: 5,
          type: 'warn',
          borderClass: 'border-red',
          bgClass: 'bg-pink',
          numberClass: 'text-red',
          label: this.$t('tablePrice.within7DaysTo3DaysAgo'),
          desc: this.$t('tablePrice.notPickedUpCount'),
          value: this.resultData.threeDaysBefore || 0
        },
        {
          key: '3to1',
          span: 4,
          type: 'warn',
          borderClass: 'border-red',
          bgClass: 'bg-pink',
          numberClass: 'text-red',
          label: this.$t('tablePrice.within3DaysTo1DayAgo'),
          desc: this.$t('tablePrice.notPickedUpCount'),
          value: this.resultData.oneDaysBefore || 0
        }
      ];
    }
  },
  mounted() {
    this.getTimer();
    this.getSearch();
    this.getAddress();
  },
  methods: {
    initChart() {
      const chartDom = this.$refs.chartDom;
      const myChart = echarts.init(chartDom, null, {
        renderer: 'canvas'
      });

      // ✅ 假数据：14个客户，按截图趋势（从左到右：84, 75, 65, 62, 60, 58, 55, 53, 50, 45, 40, 35, 30, 25）
      const customerNames = this.customerNames;
      const values = this.packageNumberList;
      const option = {
        title: {
          text: this.$t('tablePrice.customerPendingPackageStatistics'),
          left: 'left',
          top: 10,
          textStyle: {
            fontSize: 14,
            color: '#2d9a4f' // 绿色标题
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: '{b}<br/>{c}'
        },
        xAxis: {
          type: 'category',
          data: customerNames,
          axisLabel: {
            rotate: 30, // 斜排避免重叠（符合常见设计）
            fontSize: 12,
            margin: 12
          }
        },
        yAxis: {
          type: 'value',
          min: 0,
          splitLine: {
            show: true,
            lineStyle: {
              color: '#eee'
            }
          },
          axisLabel: {
            fontSize: 12
          }
        },
        series: [
          {
            name: '待接收包裹数',
            type: 'bar',
            barWidth: '20%', // 控制柱宽
            itemStyle: {
              color: '#2d9a4f', // 主绿色（与标题一致）
              borderRadius: [4, 4, 0, 0] // 顶部圆角
            },
            data: values,
            label: {
              show: true,
              position: 'top',
              formatter: '{c}',
              fontSize: 12,
              fontWeight: 'bold',
              color: '#333'
            }
          }
        ]
      };

      myChart.setOption(option);
      window.addEventListener('resize', () => {
        myChart.resize();
      });
    },
    // ✅ 构建时间参数（消除3处重复的时间校验逻辑）
    _buildTimeParams(beginKey = 'beginTime', endKey = 'endTime') {
      if (!this.timeValue || this.timeValue.length === 0) {
        this.$message.error(this.$t('tablePrice.selectTimeRange'));
        return null;
      }
      return { [beginKey]: this.timeValue[0], [endKey]: this.timeValue[1] };
    },

    // ✅ 构建公共查询参数
    _buildBaseParams() {
      return {
        customerId: this.queryParams.customerId || null,
        orderWaybillType: this.queryParams.isTest,
        senderStateList: this.queryParams.senderStateList || []
      };
    },

    getSearch() {
      const timeParams = this._buildTimeParams();
      if (!timeParams) return;

      const data = { ...this._buildBaseParams(), ...timeParams };
      bulkyWasteCollectBoardQuery(data).then((res) => {
        if (res.code === 200) {
          this.resultData = res.data;
          // 分别提取 customerName 和 packageNumber
          this.customerNames = res.data.customerDetailList.map((item) => item.customerName);
          this.packageNumberList = res.data.customerDetailList.map((item) => item.packageNumber);
          // ✅ 通过配置映射生成大区数据（消除硬编码）
          this.regionData = REGION_FIELD_MAP.map((cfg) => ({
            name: this.$t(cfg.label),
            pendingCount: res.data[cfg.key],
            customerCount: res.data[cfg.customerKey],
            regionId: cfg.regionId
          }));
          if (res.data.customerDetailList && res.data.customerDetailList.length > 0) {
            this.$nextTick(() => {
              this.initChart();
            });
          }
        }
      });
    },

    handleExport(row) {
      const timeParams = this._buildTimeParams('createBeginTime', 'createEndTime');
      if (!timeParams) return;

      const data = {
        ...this._buildBaseParams(),
        ...timeParams,
        regionId: row.regionId
      };
      bulkyWasteCollectBoardExport(data).then((res) => {
        if (res.code === 200) goResourceTaskList();
      });
    },

    exportFun() {
      const timeParams = this._buildTimeParams('createBeginTime', 'createEndTime');
      if (!timeParams) return;

      const data = {
        ...this._buildBaseParams(),
        ...timeParams,
        cargoOrderStatus: [2, 30]
      };
      waybillExportAll(data).then((res) => {
        if (res.code === 200) goResourceTaskList();
      });
    },

    handleReset() {
      this.queryParams = { customerId: '', senderStateList: [], isTest: 1 };
      this.getTimer();
      this.getSearch();
    },

    getAddress() {
      getLgAreaListData({ level: 0, parentId: '' }, false).then((res) => {
        if (res.code === 200) {
          this.StateList = res.data.map((item) => ({ id: item.id, label: item.nameEn }));
        }
      });
    },

    getCustomerPage(name = '') {
      apiCustomerPage({ pageSize: 10, pageNumber: 1, params: { quickSearch: name }}).then(
        (res) => {
          if (res.code === 200) this.customerList = res.data.records;
        }
      );
    },

    getTimer() {
      const brazilTime = new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' });
      const today = dayjs(brazilTime);
      const oneMonthAgo = today.subtract(1, 'month').format('YYYY-MM-DD');
      const endDate = today.format('YYYY-MM-DD');
      this.timeValue = [`${oneMonthAgo} 00:00:00`, `${endDate} 23:59:59`];
    }
  }
};
</script>

<style scoped>
/* ✅ 移除未使用的样式类：card-title, card-body, main-stat, sub-stat */

.filter-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 20px;
}

.filter-item {
  display: flex;
  align-items: center;
  margin-right: 20px;
}

.filter-item .label {
  margin-right: 10px;
  font-size: 14px;
  color: #606266;
  font-weight: bold;
}

.filter-actions {
  display: flex;
  align-items: center;
}

.text-red {
  color: #e16363;
}

.text-green {
  color: #01a122;
  cursor: pointer;
}

.font-bold {
  font-weight: bold;
}

.border-green {
  border: 1px solid #01a122;
}

.border-red {
  border: 1px solid #e16363;
}

.bg-pink {
  background-color: #fef0f0;
}

.bg-light-green {
  background-color: #f0f9eb;
}

.summary-row {
  margin-bottom: 20px;
}

.stat-card {
  max-height: 170px;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
}

.summary-card {
  max-height: 170px;
}

.stat-label {
  font-size: 15px;
  color: black;
  margin-right: 15px;
  font-weight: bold;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
}

.warn-card {
  max-height: 170px;
  text-align: center;
  justify-content: center;
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.warn-label {
  font-size: 15px;
  color: black;
  margin-bottom: 5px;
  font-weight: bold;
}

.warn-desc {
  font-size: 14px;
  color: black;
  margin-bottom: 10px;
}

.warn-number {
  font-size: 24px;
  font-weight: bold;
}

.region-section {
  border: 1px solid #e4e7ed;
  padding: 15px;
  border-radius: 8px;
}

.section-header {
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: bold;
}

.region-card {
  padding: 15px;
  border-radius: 8px;
  max-height: 170px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  background-color: #fff;
}

.region-name {
  text-align: center;
  margin: 15px 0;
  font-size: 16px;
}

.region-stat {
  display: flex;
  justify-content: space-between;
  padding: 3px 15px;
  margin-bottom: 5px;
}

.region-stat .stat-label {
  font-size: 15px;
}

.region-stat .stat-number {
  font-size: 20px;
}

.region-footer {
  position: absolute;
  bottom: -2px;
  right: 1px;
}

.region-footer i {
  font-size: 18px;
}
</style>
